import config from '../config';
import Helper from "../helpers";

const apiBaseUrl = config.apiBaseUrl;

export const getVenueFeatures = async (venueKey: string): Promise<string[]> => {
    try {
        const token = await Helper.getCookie('wh-jwt');
        const response = await fetch(`${apiBaseUrl}/api/venue/${venueKey}/fetures`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            credentials: 'include'
        });

        if (!response.ok) return [];
        const data = await response.json();
        return Array.isArray(data) ? data : [];
    } catch (error) {
        console.error('Error fetching venue features:', error);
        return [];
    }
};

export const getSubscriptions = async (venueKey: string) => {
    try {
        const token = await Helper.getCookie('wh-jwt');
        const response = await fetch(`${apiBaseUrl}/api/Subscription/getVenueSubscription/${venueKey}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            credentials: 'include'
        });
        
        if (!response.ok) {
            throw new Error('Network response was not ok');
        }
        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error fetching users for venue:', error);
        throw error;
    }
}

export const createSubscription = async (
    venueKey: string,
    name: string,
    price: number,
    cycleType: number,
    cycleLength: number,
    tokensPerCycle: number,
    description: string,
    runAnchorDate: string | null = null
) => {
    const token = await Helper.getCookie('wh-jwt');
    const response = await fetch(`${apiBaseUrl}/api/Subscription/AddSubscription/${venueKey}`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
            Name: name,
            Price: price,
            CycleType: cycleType,
            CycleLength: cycleLength,
            TokensPerCycle: tokensPerCycle,
            Description: description || null,
            Currency: 'GBP',
            RunAnchorDate: runAnchorDate || null
        })
    });
    if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || err.Message || 'Failed to create subscription');
    }
    return response.json();
};

export const updateSubscription = async (venueKey: string, subscriptionId: string, tokensPerCycle: number, cycleType: number, cycleCost: number, cycleLength: number, showOnSignup: boolean, runAnchorDate: string | null = null) => {
    try {
        const token = await Helper.getCookie('wh-jwt');
        const response = await fetch(`${apiBaseUrl}/api/Subscription/updateSubscription/${venueKey}`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify({ 
                subscriptionId: parseInt(subscriptionId), 
                tokensPerCycle: tokensPerCycle,
                cycleType: cycleType,
                cycleCost: cycleCost,
                cycleLength: cycleLength,
                showOnSignup: showOnSignup,
                runAnchorDate: runAnchorDate || null
            })
        });

        if (!response.ok) {
            throw new Error('Network response was not ok');
        }
        return await response.json();
    } catch (error) {
        console.error('Error updating subscription:', error);
        throw error;
    }
}

export interface VenueAddressPayload {
    line1?: string;
    line2?: string;
    line3?: string;
    city?: string;
    county?: string;
    postCode?: string;
}

// Update venue address (PUT api/venue/{venueKey}/address)
export const updateVenueAddress = async (venueKey: string, addressData: VenueAddressPayload) => {
    const token = await Helper.getCookie('wh-jwt');
    const response = await fetch(`${apiBaseUrl}/api/venue/${venueKey}/address`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(addressData)
    });

    if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error((err as any).message || 'Failed to update venue address');
    }

    return await response.json();
};

// Update venue information
export const updateVenue = async (venueData: any) => {
    try {
        const token = await Helper.getCookie('wh-jwt');
        const venueId = await Helper.getUserVenueId();
        
        if (!venueId) {
            throw new Error('No venue ID found for current user');
        }

        const response = await fetch(`${apiBaseUrl}/api/venue/${venueId}`, {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify(venueData)
        });

        if (!response.ok) {
            throw new Error('Failed to update venue information');
        }

        return await response.json();
    } catch (error) {
        console.error('Error updating venue:', error);
        throw error;
    }
}

// Mock data to use until API is implemented
export const getMockVenueData = () => {
    return {
        id: 1,
        name: "Insight Gym",
        phone: "+44 7793 273211",
        email: "enquiries@insightgym.co.uk",
        website: "https://www.insightgym.co.uk/",
        websiteDisplay: "www.insightgym.co.uk",
        logoUrl: "/img/venueLogos/insight-gym-logo.jpg",
        address: {
            street: "123 High Street",
            city: "London",
            postcode: "SW1A 1AA",
            country: "United Kingdom"
        },
        description: "Premier fitness facility with state-of-the-art equipment and expert personal trainers.",
        openingHours: {
            monday: "06:00 - 22:00",
            tuesday: "06:00 - 22:00",
            wednesday: "06:00 - 22:00",
            thursday: "06:00 - 22:00",
            friday: "06:00 - 22:00",
            saturday: "08:00 - 20:00",
            sunday: "08:00 - 20:00"
        }
    };
}